import { useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Button, Form, Modal, Spinner, Table } from 'react-bootstrap'
import useFetch from '../../_fetch'
import { IAdminEmailAccess, IUser } from './types'

type AdminEmailAccessModalProps = {
    show: boolean
    emailAccess: IAdminEmailAccess | null
    accounts: IUser[]
    onHide: () => void
    onChanged: () => Promise<void> | void
    onManageAccount: (username: string) => void
}

export default function AdminEmailAccessModal({
    show,
    emailAccess,
    accounts,
    onHide,
    onChanged,
    onManageAccount,
}: AdminEmailAccessModalProps) {
    const { doFetch } = useFetch()
    const [accountUsername, setAccountUsername] = useState('')
    const [role, setRole] = useState<'manager' | 'member'>('member')
    const [busy, setBusy] = useState('')
    const [message, setMessage] = useState<{ variant: 'success' | 'danger', text: string } | null>(null)

    const availableAccounts = useMemo(() => {
        const existing = new Set(emailAccess?.accounts.map((account) => account.username) || [])
        return accounts
            .filter((account) => !account.isAdmin && !existing.has(account.username))
            .sort((left, right) => left.username.localeCompare(right.username))
    }, [accounts, emailAccess])
    const activeAccounts = emailAccess?.accounts.filter((account) => account.status === 'active') || []

    useEffect(() => {
        if (!show) return
        setRole('member')
        setBusy('')
        setMessage(null)
    }, [emailAccess?.email, show])

    useEffect(() => {
        if (!show) return
        setAccountUsername((current) => availableAccounts.some((account) => account.username === current)
            ? current
            : availableAccounts[0]?.username || '')
    }, [availableAccounts, show])

    if (!emailAccess) return null

    const run = async (key: string, action: () => Promise<Response>, success: string | ((body: any) => string)) => {
        setBusy(key)
        setMessage(null)
        try {
            const response = await action()
            if (!response.ok) throw new Error(await response.text())
            const body = typeof success === 'function' ? await response.json() : null
            await onChanged()
            setMessage({ variant: 'success', text: typeof success === 'function' ? success(body) : success })
        } catch (error) {
            await Promise.resolve(onChanged()).catch(() => undefined)
            setMessage({ variant: 'danger', text: error instanceof Error ? error.message : 'The action failed.' })
        } finally {
            setBusy('')
        }
    }

    const grantAccess = async (event: React.FormEvent) => {
        event.preventDefault()
        if (!accountUsername) return
        await run(
            'grant',
            () => doFetch(`/user/admin/accounts/${encodeURIComponent(accountUsername)}/access/invitations`, { email: emailAccess.email, role }),
            (body) => body?.provisioning === 'direct'
                ? `Access granted to ${accountUsername}. No e-mail was sent.`
                : `Invitation sent for ${accountUsername}.`,
        )
    }

    const sendPasswordReset = () => run(
        'password-reset',
        () => doFetch('/user/password-reset/request', { email: emailAccess.email }),
        'Password reset e-mail requested.',
    )

    return (
        <Modal show={show} onHide={onHide} size="lg" centered scrollable dialogClassName="admin-email-modal">
            <Modal.Header closeButton>
                <div>
                    <div className="user-management-eyebrow">E-mail identity</div>
                    <Modal.Title as="h5">{emailAccess.email}</Modal.Title>
                    <div className="d-flex flex-wrap gap-2 mt-2">
                        <Badge bg={emailAccess.isVerified ? 'success' : 'warning'} text={emailAccess.isVerified ? undefined : 'dark'}>
                            {emailAccess.isVerified ? 'Verified identity' : emailAccess.hasIdentity ? 'Unverified identity' : 'Invitation only'}
                        </Badge>
                        <Badge bg="light" text="dark" className="border">{emailAccess.accounts.length} account{emailAccess.accounts.length === 1 ? '' : 's'}</Badge>
                    </div>
                </div>
            </Modal.Header>
            <Modal.Body>
                {message && <Alert variant={message.variant} aria-live="polite">{message.text}</Alert>}

                {emailAccess.hasIdentity && !!activeAccounts.length && (
                    <div className="user-management-primary-panel mb-4">
                        <div>
                            <div className="fw-semibold">Primary account</div>
                            <div className="text-muted small mt-1">This is the first account opened when the person signs in by e-mail.</div>
                        </div>
                        <Form.Select
                            aria-label="Primary account"
                            value={emailAccess.primaryAccount || ''}
                            disabled={!!busy}
                            onChange={(event) => run(
                                'primary',
                                () => doFetch(`/user/admin/emails/${encodeURIComponent(emailAccess.email)}/primary-account`, { username: event.target.value }, 'PATCH'),
                                'Primary account updated.',
                            )}
                        >
                            {!emailAccess.primaryAccount && <option value="">Select an account</option>}
                            {activeAccounts.map((account) => <option key={account.username} value={account.username}>{account.username}</option>)}
                        </Form.Select>
                    </div>
                )}

                <div className="user-management-section-heading">
                    <h6>Account access</h6>
                    <p>Change the role or remove this e-mail from an account.</p>
                </div>
                <div className="user-management-access-table">
                    <Table responsive className="align-middle mb-0">
                        <thead><tr><th>Account</th><th>Status</th><th>Role</th><th className="text-end">Actions</th></tr></thead>
                        <tbody>
                            {emailAccess.accounts.length ? emailAccess.accounts.map((account) => (
                                <tr key={account.username}>
                                    <td>
                                        <button type="button" className="user-management-account-link" onClick={() => onManageAccount(account.username)}>
                                            {account.username}
                                        </button>
                                        {account.isPrimary && <div className="text-muted small">Primary account</div>}
                                    </td>
                                    <td><Badge bg={account.status === 'active' ? 'success' : 'warning'} text={account.status === 'pending' ? 'dark' : undefined} className="text-capitalize">{account.status}</Badge></td>
                                    <td className="user-management-role-cell">
                                        <Form.Select
                                            size="sm"
                                            aria-label={`Role for ${account.username}`}
                                            value={account.role}
                                            disabled={!!busy}
                                            onChange={(event) => run(
                                                `role:${account.username}`,
                                                () => doFetch(`/user/admin/accounts/${encodeURIComponent(account.username)}/access/members/${encodeURIComponent(emailAccess.email)}`, { role: event.target.value }, 'PATCH'),
                                                'Role updated.',
                                            )}
                                        >
                                            <option value="member">Member</option>
                                            <option value="manager">Manager</option>
                                        </Form.Select>
                                    </td>
                                    <td className="text-end text-nowrap">
                                        {account.status === 'pending' && (
                                            <Button
                                                size="sm"
                                                variant="outline-secondary"
                                                className="me-2"
                                                disabled={!!busy}
                                                onClick={() => run(
                                                    `resend:${account.username}`,
                                                    () => doFetch(`/user/admin/accounts/${encodeURIComponent(account.username)}/access/invitations/resend`, { email: emailAccess.email }),
                                                    'Invitation resent.',
                                                )}
                                            >Resend</Button>
                                        )}
                                        <Button
                                            size="sm"
                                            variant="outline-danger"
                                            disabled={!!busy}
                                            onClick={() => window.confirm(`Remove ${emailAccess.email} from ${account.username}?`) && run(
                                                `remove:${account.username}`,
                                                () => doFetch(`/user/admin/accounts/${encodeURIComponent(account.username)}/access/members/${encodeURIComponent(emailAccess.email)}`, undefined, 'DELETE'),
                                                'Access removed.',
                                            )}
                                        >Remove</Button>
                                    </td>
                                </tr>
                            )) : (
                                <tr><td colSpan={4} className="text-center text-muted py-4">This e-mail does not currently have account access.</td></tr>
                            )}
                        </tbody>
                    </Table>
                </div>

                <div className="user-settings-section-divider" />
                <div className="user-management-section-heading">
                    <h6>Add access to another account</h6>
                    <p>Verified users get access immediately without an e-mail. New users receive one invitation.</p>
                </div>
                <Form onSubmit={grantAccess} className="user-management-grant-panel">
                    <div className="user-management-grant-grid">
                        <Form.Group>
                            <Form.Label htmlFor="email-access-account">Account</Form.Label>
                            <Form.Select id="email-access-account" value={accountUsername} onChange={(event) => setAccountUsername(event.target.value)} disabled={!availableAccounts.length || !!busy}>
                                {!availableAccounts.length && <option value="">No available accounts</option>}
                                {availableAccounts.map((account) => <option key={account.username} value={account.username}>{account.username}</option>)}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group>
                            <Form.Label htmlFor="email-access-new-role">Role</Form.Label>
                            <Form.Select id="email-access-new-role" value={role} onChange={(event) => setRole(event.target.value as 'manager' | 'member')} disabled={!!busy}>
                                <option value="member">Member</option>
                                <option value="manager">Manager</option>
                            </Form.Select>
                        </Form.Group>
                        <Button type="submit" disabled={!accountUsername || !!busy}>
                            {busy === 'grant' ? <><Spinner animation="border" size="sm" className="me-2" />Adding…</> : 'Add access'}
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
            <Modal.Footer className="justify-content-between">
                <Button variant="outline-primary" disabled={!emailAccess.isVerified || !!busy} onClick={sendPasswordReset}>
                    {busy === 'password-reset' ? 'Sending…' : 'Send password reset'}
                </Button>
                <Button variant="outline-secondary" onClick={onHide}>Close</Button>
            </Modal.Footer>
        </Modal>
    )
}
